/**
 * The cards the agent can put up in the rail, as grouped lists: a question
 * (pick one, pick any, or type your own) and a value check (confirm or correct
 * each value), and the Plan Month counts. Live, they hold the composer shut; answered, they fold down to
 * what you picked, so the pick stays visible and the echoed message is dropped.
 */
import { useRef, useState } from "react";
import { Check, PencilLine, Sparkles, X } from "lucide-react";
import { KitStepper } from "@/app/routes/dashboard/components/kit/Switch";
import {
  calendarTitle,
  formatPlanMonthMessage,
  monthWord,
  notNowMessage,
  PLAN_MONTH_FIELDS,
  type PlanMonthAnswer,
  type PlanMonthCounts,
  type PlanMonthRequest,
} from "@/app/routes/dashboard/components/planMonth";
import type { AskUserQuestion } from "@/app/routes/dashboard/components/askUserQuestion";
import {
  isAnswered,
  useAskUserQuestionState,
} from "@/app/routes/dashboard/components/useAskUserQuestionState";
import { useVerifyState } from "@/app/routes/dashboard/components/useVerifyState";
import {
  emptyVerdict,
  type VerifyRequest,
} from "@/app/routes/dashboard/components/verifyWithUser";
import type { CardAnswer, VerifyAnswer } from "./railFormat";

/* --- questions --------------------------------------------------------------------- */

export function RailQuestionCard({
  questions,
  onSubmit,
  disabled = false,
}: {
  questions: AskUserQuestion[];
  /** Absent while the turn that raised it is still streaming. */
  onSubmit?: (message: string) => void;
  disabled?: boolean;
}) {
  const {
    answers,
    setActive,
    locked,
    single,
    answeredCount,
    complete,
    activeIndex,
    question,
    answer,
    jumpTarget,
    submit,
    skip,
    toggleOption,
    setFreeText,
    onFreeTextEnter,
    goNext,
  } = useAskUserQuestionState(questions, onSubmit, disabled);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const live = Boolean(onSubmit);

  const focusTab = (index: number) => {
    setActive(index);
    tabRefs.current[index]?.focus();
  };

  return (
    <section className="m7r-card" data-live={live || undefined} aria-label="The agent is asking">
      {single ? null : (
        <div className="m7r-card-tabs" role="tablist" aria-label="Questions">
          {questions.map((q, i) => {
            const selected = i === activeIndex;
            return (
              <button
                key={`${i}-${q.header}`}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                type="button"
                role="tab"
                id={`m7r-ask-tab-${i}`}
                aria-selected={selected}
                aria-controls={`m7r-ask-panel-${i}`}
                tabIndex={selected ? 0 : -1}
                className="m7r-card-tab"
                data-answered={isAnswered(answers[i]) || undefined}
                onClick={() => setActive(i)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowRight") {
                    e.preventDefault();
                    focusTab((i + 1) % questions.length);
                  } else if (e.key === "ArrowLeft") {
                    e.preventDefault();
                    focusTab((i - 1 + questions.length) % questions.length);
                  }
                }}
              >
                <i aria-hidden />
                <span>{q.header}</span>
              </button>
            );
          })}
        </div>
      )}

      <div
        key={activeIndex}
        role={single ? undefined : "tabpanel"}
        id={single ? undefined : `m7r-ask-panel-${activeIndex}`}
        aria-labelledby={single ? undefined : `m7r-ask-tab-${activeIndex}`}
      >
        {/* The kicker: the question's header and how many are answered. The Workshop
            draws it (07 Workshop, Workshop / Question card); the rail keeps it hidden. */}
        <div className="m7r-card-kicker" aria-hidden>
          <span>{question.header}</span>
          <span>
            {answeredCount} of {questions.length} answered
          </span>
        </div>
        <div className="m7r-card-head">
          <p className="m7r-card-question">{question.question}</p>
          <p className="m7r-card-hint">
            {question.multiSelect ? "Pick any" : "Pick one"}
            {single ? "" : `, question ${activeIndex + 1} of ${questions.length}`}
          </p>
        </div>

        <div
          role={question.multiSelect ? "group" : "radiogroup"}
          aria-label={question.question}
        >
          {question.options.map((option) => {
            const on = answer.selected.includes(option.label);
            return (
              <button
                key={option.label}
                type="button"
                role={question.multiSelect ? "checkbox" : "radio"}
                aria-checked={on}
                disabled={locked}
                className="m7r-option"
                data-on={on || undefined}
                onClick={() => toggleOption(activeIndex, option.label)}
              >
                {/* Pick any is a checkbox; pick one is a square radio, so the two
                    never look alike. A pick tints its whole row. */}
                <span
                  className="m7r-pick"
                  data-kind={question.multiSelect ? "check" : "radio"}
                  data-on={on || undefined}
                  aria-hidden
                >
                  {on && question.multiSelect ? <Check strokeWidth={2.5} /> : null}
                </span>
                <span className="m7r-option-text">
                  <span>{option.label}</span>
                  {option.description ? <small>{option.description}</small> : null}
                </span>
              </button>
            );
          })}
          <label className="m7r-own">
            <PencilLine aria-hidden strokeWidth={1.75} />
            <input
              key={activeIndex}
              value={answer.freeText}
              disabled={locked}
              placeholder="Or type your own answer"
              aria-label={`Your own answer to: ${question.question}`}
              onChange={(e) => setFreeText(activeIndex, e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter" || e.shiftKey) return;
                e.preventDefault();
                onFreeTextEnter();
              }}
            />
          </label>
        </div>
      </div>

      {live ? (
        <div className="m7r-card-actions">
          <button
            type="button"
            className="m7r-text-btn"
            disabled={locked}
            onClick={skip}
            title="Let the agent decide and carry on"
          >
            Skip
          </button>
          {!single && !complete ? (
            <span className="m7r-card-progress">
              {answeredCount} of {questions.length} answered
            </span>
          ) : null}
          {complete ? (
            <button type="button" className="m7r-primary" disabled={locked} onClick={() => submit()}>
              {single ? "Send answer" : "Send answers"}
            </button>
          ) : jumpTarget !== null ? (
            <button type="button" className="m7r-primary" disabled={locked} onClick={goNext}>
              Next question
            </button>
          ) : (
            <button type="button" className="m7r-primary" disabled>
              {single ? "Send answer" : "Send answers"}
            </button>
          )}
        </div>
      ) : null}
    </section>
  );
}

/** A question the user already answered: what was asked, and what they picked. */
export function QuestionAnswered({
  questions,
  answer,
}: {
  questions: AskUserQuestion[];
  answer: CardAnswer | null;
}) {
  return (
    <div className="m7r-answered">
      {questions.map((q, i) => (
        <div key={`${i}-${q.question}`} className="m7r-answered-item">
          <p className="m7r-answered-q">{q.question}</p>
          {answer === null ? (
            <p className="m7r-answered-skip">{q.options.map((o) => o.label).join(", ")}</p>
          ) : answer.skipped ? (
            i === 0 ? (
              <p className="m7r-answered-skip">Skipped. The agent carried on with its own judgement.</p>
            ) : null
          ) : (
            <div className="m7r-answered-pick">
              <Check aria-hidden strokeWidth={2} />
              <span>{answer.values[i] || "No answer"}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* --- value checks ------------------------------------------------------------------- */

const VERIFY_INTRO = "I found these but couldn't confirm them. Check each one.";

export function RailVerifyCard({
  request,
  onSubmit,
  disabled = false,
}: {
  request: VerifyRequest;
  onSubmit?: (message: string) => void;
  disabled?: boolean;
}) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const {
    items,
    verdicts,
    locked,
    complete,
    settledCount,
    anyUnset,
    submit,
    skip,
    rule,
    setCorrection,
    confirmAll,
    onCorrectionEnter,
  } = useVerifyState(request, onSubmit, disabled, (index) => inputRefs.current[index]?.focus());
  const live = Boolean(onSubmit);

  return (
    <section className="m7r-card" data-live={live || undefined} aria-label="Values to check">
      <div className="m7r-card-head">
        <p className="m7r-card-question">{request.intro ?? VERIFY_INTRO}</p>
        {live ? (
          <div className="m7r-card-hintrow">
            <p className="m7r-card-hint">Right or wrong, one at a time</p>
            {items.length > 1 && anyUnset ? (
              <button
                type="button"
                className="m7r-text-btn m7r-text-btn--accent"
                disabled={locked}
                onClick={confirmAll}
              >
                Confirm the rest
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {items.map((item, index) => {
        const verdict = verdicts[index] ?? emptyVerdict();
        const rejected = verdict.state === "rejected";
        return (
          <div key={`${index}-${item.label}`} className="m7r-verify-row">
            <div className="m7r-verify-top">
              <div className="m7r-verify-main">
                <span className="m7r-verify-label">{item.label}</span>
                <span
                  className="m7r-verify-value"
                  data-empty={!item.value || undefined}
                  data-rejected={(rejected && Boolean(item.value)) || undefined}
                >
                  {item.value || "Nothing found"}
                </span>
                {item.note ? <span className="m7r-verify-note">{item.note}</span> : null}
              </div>
              <div className="m7r-verdicts" role="radiogroup" aria-label={`Is ${item.label} right?`}>
                <button
                  type="button"
                  role="radio"
                  className="m7r-verdict"
                  data-kind="yes"
                  aria-checked={verdict.state === "confirmed"}
                  aria-label={`${item.label} is right`}
                  title="Right"
                  disabled={locked}
                  onClick={() => rule(index, "confirmed")}
                >
                  <Check aria-hidden strokeWidth={2.25} />
                </button>
                <button
                  type="button"
                  role="radio"
                  className="m7r-verdict"
                  data-kind="no"
                  aria-checked={rejected}
                  aria-label={`${item.label} is wrong`}
                  title="Wrong"
                  disabled={locked}
                  onClick={() => rule(index, "rejected")}
                >
                  <X aria-hidden strokeWidth={2.25} />
                </button>
              </div>
            </div>
            {rejected ? (
              <input
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                className="m7r-verify-fix"
                value={verdict.correction}
                disabled={locked}
                placeholder={
                  item.value
                    ? "The right one, or leave blank if there isn't one"
                    : "Add it, or leave blank if you don't have it"
                }
                aria-label={`Correct value for ${item.label}`}
                onChange={(e) => setCorrection(index, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" || e.shiftKey) return;
                  e.preventDefault();
                  onCorrectionEnter(index);
                }}
              />
            ) : null}
          </div>
        );
      })}
      {live ? (
        <div className="m7r-card-actions">
          <button
            type="button"
            className="m7r-text-btn"
            disabled={locked}
            onClick={skip}
            title="Leave these unconfirmed and carry on"
          >
            Skip
          </button>
          {!complete ? (
            <span className="m7r-card-progress">
              {settledCount} of {items.length} checked
            </span>
          ) : null}
          <button type="button" className="m7r-primary" disabled={locked || !complete} onClick={submit}>
            Send
          </button>
        </div>
      ) : null}
    </section>
  );
}

/** A value check the user already ruled on. */
export function VerifyAnswered({
  request,
  answer,
}: {
  request: VerifyRequest;
  answer: VerifyAnswer | null;
}) {
  return (
    <div className="m7r-answered">
      <p className="m7r-answered-q">{request.intro ?? VERIFY_INTRO}</p>
      {answer?.skipped ? (
        <p className="m7r-answered-skip">Skipped. These stay unconfirmed.</p>
      ) : (
        request.items.map((item, i) => {
          const row = answer && !answer.skipped ? answer.rows[i] : null;
          return (
            <div key={`${i}-${item.label}`} className="m7r-answered-item" data-tight>
              <span className="m7r-verify-label">{item.label}</span>
              {row?.state === "confirmed" ? (
                <div className="m7r-answered-pick">
                  <Check aria-hidden strokeWidth={2} />
                  <span>{item.value || "There is none"}</span>
                </div>
              ) : row?.state === "corrected" ? (
                <div className="m7r-answered-pick">
                  <PencilLine aria-hidden strokeWidth={1.75} />
                  <span>{row.correction}</span>
                </div>
              ) : row?.state === "wrong" ? (
                <div className="m7r-answered-pick">
                  <X aria-hidden strokeWidth={2} />
                  <span>Not right, and no replacement given</span>
                </div>
              ) : (
                <span className="m7r-verify-value" data-empty={!item.value || undefined}>
                  {item.value || "Nothing found"}
                </span>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

/* --- plan month -------------------------------------------------------------------- */

/** The server's cap on a month, across the three formats. */
const PLAN_MONTH_MAX_TOTAL = 30;

const PLAN_SOURCE_LINE: Record<PlanMonthRequest["source"], string> = {
  MONTH: "This month’s own numbers.",
  STRATEGY: "From the strategy’s monthly volume and format mix.",
  SETTINGS: "From the brand’s calendar settings.",
};

/**
 * The Plan Month card (library Calendar / Scope card): the month's posts a
 * month and its three counts, for this month only, then Not now or Plan
 * {month}. Neither saves anything here; the pick goes to the agent as a
 * message, and the agent runs the plan with it.
 */
export function RailPlanMonthCard({
  request,
  onSubmit,
  disabled = false,
}: {
  request: PlanMonthRequest;
  /** Absent while the turn that raised it is still streaming. */
  onSubmit?: (message: string) => void;
  disabled?: boolean;
}) {
  const [counts, setCounts] = useState<PlanMonthCounts>({
    staticPosts: request.staticPosts,
    verticalVideoPosts: request.verticalVideoPosts,
    horizontalVideoPosts: request.horizontalVideoPosts,
  });
  const [sent, setSent] = useState(false);
  const live = Boolean(onSubmit);
  const locked = !live || disabled || sent;
  const total = counts.staticPosts + counts.verticalVideoPosts + counts.horizontalVideoPosts;
  const problem =
    total < 1
      ? "A month needs at least one post."
      : total > PLAN_MONTH_MAX_TOTAL
        ? `That is ${total} posts; a month takes at most ${PLAN_MONTH_MAX_TOTAL}.`
        : null;
  const changed = PLAN_MONTH_FIELDS.some((field) => counts[field.key] !== request[field.key]);
  const send = (message: string) => {
    if (locked || !onSubmit) return;
    setSent(true);
    onSubmit(message);
  };

  return (
    <section className="m7r-card m7r-plan" data-live={live || undefined} aria-label={`Plan ${request.monthLabel}`}>
      <div className="m7r-card-head">
        <p className="m7r-card-question">{calendarTitle(request.brandName)}</p>
        <p className="m7r-card-hint">These numbers are for {monthWord(request)} only. Every other month keeps its own.</p>
      </div>
      <div className="m7r-plan-body">
        <div className="m7r-plan-total">
          <span className="m7r-plan-kicker">Posts a month</span>
          <span className="m7r-plan-value" aria-live="polite">
            {total} {total === 1 ? "post" : "posts"}
          </span>
          <span className="m7r-plan-note" data-alert={problem ? true : undefined} role={problem ? "alert" : undefined}>
            {problem ??
              (changed
                ? `Set for ${monthWord(request)} only. The brand’s settings stay as they are.`
                : request.prorated
                  ? `${PLAN_SOURCE_LINE[request.source]} Fewer, since the month is under way.`
                  : PLAN_SOURCE_LINE[request.source])}
          </span>
        </div>
        <div className="m7r-plan-rows">
          {PLAN_MONTH_FIELDS.map((field) => (
            <div key={field.key} className="m7r-plan-row">
              <span>{field.label}</span>
              <KitStepper
                label={field.label}
                value={counts[field.key]}
                min={0}
                max={PLAN_MONTH_MAX_TOTAL}
                disabled={locked}
                onChange={(n) => setCounts((current) => ({ ...current, [field.key]: n }))}
              />
            </div>
          ))}
        </div>
      </div>
      {live ? (
        <div className="m7r-card-actions">
          <button type="button" className="m7r-text-btn" disabled={locked} onClick={() => send(notNowMessage(request))}>
            Not now
          </button>
          <button
            type="button"
            className="m7r-primary m7r-plan-go"
            disabled={locked || !!problem}
            onClick={() => send(formatPlanMonthMessage(request, counts))}
          >
            Plan {monthWord(request)}
            <Sparkles aria-hidden strokeWidth={2} />
          </button>
        </div>
      ) : null}
    </section>
  );
}

/** A Plan Month card the user already answered: the counts it was planned with, or Not now. */
export function PlanMonthAnswered({ request, answer }: { request: PlanMonthRequest; answer: PlanMonthAnswer | null }) {
  return (
    <div className="m7r-answered">
      <p className="m7r-answered-q">Plan {request.monthLabel}</p>
      {answer && !answer.planned ? (
        <p className="m7r-answered-skip">Not now. Nothing was planned.</p>
      ) : (
        <div className="m7r-answered-pick">
          <Check aria-hidden strokeWidth={2} />
          <span>
            {PLAN_MONTH_FIELDS.map((field) => `${(answer ?? request)[field.key]} ${field.word}`).join(" · ")}
          </span>
        </div>
      )}
    </div>
  );
}
